// src/contexts/ProgressContext.js
import React, { createContext, useContext, useState, useCallback } from "react";

const ProgressContext = createContext(null);

export const useProgress = () => {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error("useProgress must be used within ProgressProvider");
  return ctx;
};

export const ProgressProvider = ({ children }) => {
  // percent: 0-100
  const [percent, setPercent] = useState(0);
  // stepText: "Creating Category → Creating Services → Complete"
  const [stepText, setStepText] = useState("");
  // detailed logs (optional)
  const [logs, setLogs] = useState([]);
  // total and completed for internal computation
  const [totalSteps, setTotalSteps] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [visible, setVisible] = useState(false);

  const start = useCallback(({ total, initialStepText }) => {
    setTotalSteps(total);
    setCompletedSteps(0);
    setPercent(total === 0 ? 100 : Math.floor((0 / total) * 100));
    setStepText(initialStepText || "");
    setLogs([]);
    setVisible(true);
  }, []);

  const advance = useCallback(({ increment = 1, stepText: newStep, log }) => {
    setCompletedSteps(prev => {
      const next = prev + increment;
      if (totalSteps > 0) {
        setPercent(Math.round((next / totalSteps) * 100));
      } else {
        setPercent(100);
      }
      return next;
    });
    if (newStep) setStepText(newStep);
    if (log) setLogs(prev => [...prev, log]);
  }, [totalSteps]);

  const complete = useCallback(() => {
    setCompletedSteps(totalSteps);
    setPercent(100);
    setStepText("Complete");
    setVisible(true);
  }, [totalSteps]);

  const reset = useCallback(() => {
    setPercent(0);
    setStepText("");
    setLogs([]);
    setTotalSteps(0);
    setCompletedSteps(0);
    setVisible(false);
  }, []);

  return (
    <ProgressContext.Provider value={{
      percent,
      stepText,
      logs,
      visible,
      start,
      advance,
      complete,
      reset,
      setVisible
    }}>
      {children}
    </ProgressContext.Provider>
  );
};
